/**
 * Packing List comercial en Excel — misma composición que el PDF.
 */
import ExcelJS from 'exceljs';
import ptcImage from '../../imagenes/ptc.jpg';
import summaryStampImage from '../../imagenes/palmtrade-summary-stamp.png';
import { invoiceNumberCorrelativo } from '../invoice/invoiceNumberUtils';
import {
  packingListDetailRowsForExcel,
  packingListPdfFileName,
} from './generatePackingListPDF';
import { resolvePackingListSummaryText } from './packingListSummaryText';
import {
  BORDER,
  fmtDateLongEn,
  fmtNumCL,
  safe,
  setColWidths,
  styleRange,
  upper,
  workbookToDownload,
} from '../documentos/commercialExcelUtils';

const COMPANY_LINES = [
  'PALM TRADE CHILE SPA',
  'Avenida Presidente Kennedy 5146, oficina 51',
  'Vitacura, Santiago, Chile',
  'Fono: (56-2) 22190119 - Fax: (56-2) 22184390',
];

const WHITE_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFFFFFF' },
};

const DOCUMENT_COLUMN_OFFSET = 1;

async function imageUrlToDataUrl(url, mimeType = 'image/jpeg') {
  const response = await fetch(url);
  if (!response.ok) throw new Error('No se pudo cargar el logo del Packing List.');
  const bytes = new Uint8Array(await response.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return `data:${mimeType};base64,${btoa(binary)}`;
}

const DETAIL_HEAD_1 = [
  'Contract',
  'Products',
  'Bundles',
  'Measures',
  '',
  '',
  'Pieces per Bundles',
  'Total pieces',
  'cbm or m3 per Bundles',
  'Total Vol. cbm or m3',
  'Gross Weight kgs',
  'Net Weight kgs',
  'Container',
];

const DETAIL_HEAD_2 = [
  '',
  '',
  '',
  'Thickness',
  'Width',
  'Length',
  '',
  '',
  '',
  '',
  '',
  '',
  '',
];

function fmtIntegerCL(v) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '';
  return new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(v));
}

function packingListExcelFileName(params) {
  return packingListPdfFileName(params).replace(/\.pdf$/i, '.xlsx');
}

function normalizeCountryForPackingList(value) {
  const text = safe(value).trim();
  return /^brasil$/i.test(text) ? 'Brazil' : text;
}

function portNameWithoutCountry(value) {
  return safe(value).trim().replace(/\s*\([^)]*\)\s*$/, '').trim();
}

function portNameCommaCountry(value) {
  const text = safe(value).trim().replace(/\((\s*Brasil\s*)\)/i, '(Brazil)');
  const match = text.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (!match) return text;
  return `${match[1].trim()}, ${normalizeCountryForPackingList(match[2])}`;
}

function setPlainMergedCell(ws, row, colFrom, colTo, value, options = {}) {
  const shiftedFrom = colFrom + DOCUMENT_COLUMN_OFFSET;
  const shiftedTo = colTo + DOCUMENT_COLUMN_OFFSET;
  if (shiftedFrom !== shiftedTo) ws.mergeCells(row, shiftedFrom, row, shiftedTo);
  const cell = ws.getCell(row, shiftedFrom);
  cell.value = value || null;
  cell.font = {
    name: 'Arial',
    size: 8,
    color: { argb: 'FF000000' },
    ...(options.font || {}),
  };
  cell.alignment = {
    horizontal: 'left',
    vertical: 'top',
    wrapText: true,
    ...(options.alignment || {}),
  };
}

function setMergedBlock(ws, rowFrom, colFrom, rowTo, colTo, value, options = {}) {
  const shiftedFrom = colFrom + DOCUMENT_COLUMN_OFFSET;
  const shiftedTo = colTo + DOCUMENT_COLUMN_OFFSET;
  if (rowFrom !== rowTo || shiftedFrom !== shiftedTo) {
    ws.mergeCells(rowFrom, shiftedFrom, rowTo, shiftedTo);
  }
  const cell = ws.getCell(rowFrom, shiftedFrom);
  cell.value = value || null;
  cell.font = {
    name: 'Arial',
    size: 8,
    color: { argb: 'FF000000' },
    ...(options.font || {}),
  };
  cell.alignment = {
    horizontal: 'center',
    vertical: 'middle',
    wrapText: true,
    ...(options.alignment || {}),
  };
  styleRange(ws, rowFrom, shiftedFrom, rowTo, shiftedTo, {
    border: BORDER,
    fill: WHITE_FILL,
  });
}

function writeDocumentRow(ws, rowIndex, values, options = {}) {
  const row = ws.getRow(rowIndex);
  values.forEach((value, index) => {
    const cell = row.getCell(index + 1 + DOCUMENT_COLUMN_OFFSET);
    cell.value = value === '' || value === undefined ? null : value;
    if (options.border) cell.border = BORDER;
    if (options.font) cell.font = { ...cell.font, ...options.font };
    if (options.fill) cell.fill = options.fill;
    if (options.align) cell.alignment = options.align;
    if (options.wrap) {
      cell.alignment = { ...(cell.alignment || {}), wrapText: true, ...options.align };
    }
  });
  if (options.height) row.height = options.height;
  return row;
}

export async function buildPackingListExcelWorkbook(params) {
  const {
    numeroInvoice,
    fechaEmbarque,
    clienteNombre,
    clienteDireccion,
    clienteCodigoPostal,
    poLabel,
    paisOrigen,
    paisDestino,
    nombreNave,
    nombrePuertoOrigen,
    nombrePuertoDestino,
    lugarEntrega,
    nombreBl,
    totalPaquetes,
    totalContenedores,
    detalleExcel = [],
    totalVolumenM3,
    productDescription = '',
    hsCode = '',
    summaryAdditionalText = '',
  } = params || {};

  const wb = new ExcelJS.Workbook();
  wb.creator = 'PalmTrade ERP';
  const ws = wb.addWorksheet('Packing List', {
    views: [{ showGridLines: false }],
    pageSetup: {
      orientation: 'landscape',
      paperSize: 9,
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: { left: 0.3, right: 0.3, top: 0.35, bottom: 0.35, header: 0, footer: 0 },
    },
  });

  setColWidths(ws, [3, 12, 22, 10, 10, 10, 10, 12, 10, 12, 12, 12, 12, 14]);

  const logoId = wb.addImage({
    base64: await imageUrlToDataUrl(ptcImage),
    extension: 'jpeg',
  });
  ws.addImage(logoId, {
    tl: { col: 1.2, row: 0.25 },
    ext: { width: 102, height: 88 },
    editAs: 'oneCell',
  });

  [18, 22, 19, 18, 16, 14].forEach((height, index) => {
    ws.getRow(index + 1).height = height;
  });
  ws.mergeCells('K2:N2');
  ws.getCell('K2').value = 'Packing List';
  ws.getCell('K2').font = { name: 'Arial', bold: true, size: 16, color: { argb: 'FF000000' } };
  ws.getCell('K2').alignment = { horizontal: 'right', vertical: 'middle' };
  ws.mergeCells('K3:N3');
  ws.getCell('K3').value = invoiceNumberCorrelativo(numeroInvoice) || '';
  ws.getCell('K3').font = { name: 'Arial', bold: true, size: 13, color: { argb: 'FF000000' } };
  ws.getCell('K3').alignment = { horizontal: 'right', vertical: 'middle' };
  ws.mergeCells('K4:N4');
  ws.getCell('K4').value = `Date: ${fmtDateLongEn(fechaEmbarque)}`;
  ws.getCell('K4').font = { name: 'Arial', size: 9, color: { argb: 'FF000000' } };
  ws.getCell('K4').alignment = { horizontal: 'right', vertical: 'middle' };

  let r = 7;
  for (const line of COMPANY_LINES) {
    ws.mergeCells(r, 2, r, 7);
    writeDocumentRow(ws, r, [line], { font: { name: 'Arial', size: 8, color: { argb: 'FF000000' } } });
    ws.getRow(r).height = 12;
    r += 1;
  }
  r += 1;

  const addrLines = safe(clienteDireccion)
    .split(/\r?\n/)
    .map((ln) => ln.trim())
    .filter(Boolean)
    .filter((ln) => upper(normalizeCountryForPackingList(ln)) !== upper(normalizeCountryForPackingList(paisDestino)));
  if (clienteCodigoPostal && !addrLines.some((ln) => /^PIN CODE\s*:/i.test(ln))) {
    addrLines.push(`PIN CODE: ${safe(clienteCodigoPostal).trim()}`);
  }
  const customerBlock = [upper(clienteNombre), ...addrLines.map(upper)].filter(Boolean).join('\n');
  const customerHeight = Math.max(64, (addrLines.length + 2) * 12);
  ws.getRow(r).height = customerHeight / 2;
  ws.getRow(r + 1).height = customerHeight / 2;
  setMergedBlock(ws, r, 1, r + 1, 6, {
    richText: [
      { font: { name: 'Arial', bold: true, size: 8 }, text: 'Customer:\n' },
      { font: { name: 'Arial', size: 8 }, text: customerBlock },
    ],
  }, {
    alignment: { horizontal: 'left', vertical: 'top', wrapText: true },
  });
  setMergedBlock(ws, r, 7, r, 7, 'Contract\nNumber', {
    font: { bold: true },
    alignment: { horizontal: 'left' },
  });
  setMergedBlock(ws, r, 8, r, 11, upper(poLabel));
  setMergedBlock(ws, r + 1, 7, r + 1, 7, 'Country of\nOrigin', {
    font: { bold: true },
    alignment: { horizontal: 'left' },
  });
  setMergedBlock(ws, r + 1, 8, r + 1, 11, upper(normalizeCountryForPackingList(paisOrigen)));
  r += 2;

  const {
    rows: detailRows,
    mergeSpans,
    sumM3,
    sumPesoGross,
    sumPesoNet,
  } = packingListDetailRowsForExcel(detalleExcel, totalVolumenM3);
  const shipHeaders = [
    'Total Packages',
    'Total cbm or m³',
    'Container',
    'Destination Country',
    'Vessel Name',
    'Loading Port',
    'Unloading Port',
    'Place of delivery',
    'BL',
  ];
  const shipValues = [
    totalPaquetes != null && totalPaquetes !== '' ? String(totalPaquetes) : '',
    sumM3 != null ? fmtNumCL(sumM3, 3) : '',
    totalContenedores != null && totalContenedores !== '' ? String(totalContenedores) : '',
    upper(normalizeCountryForPackingList(paisDestino)),
    upper(nombreNave),
    upper(portNameWithoutCountry(nombrePuertoOrigen)),
    upper(portNameCommaCountry(nombrePuertoDestino)),
    upper(lugarEntrega),
    upper(nombreBl),
  ];
  const shipRanges = [
    [1, 1],
    [2, 2],
    [3, 3],
    [4, 5],
    [6, 7],
    [8, 8],
    [9, 9],
    [10, 10],
    [11, 11],
  ];
  ws.getRow(r).height = 30;
  shipRanges.forEach(([from, to], index) => {
    setMergedBlock(ws, r, from, r, to, shipHeaders[index], { font: { bold: true, size: 7 } });
  });
  r += 1;
  ws.getRow(r).height = 34;
  shipRanges.forEach(([from, to], index) => {
    setMergedBlock(ws, r, from, r, to, shipValues[index], { font: { size: 8 } });
  });
  r += 2;

  const headR1 = r;
  writeDocumentRow(ws, r, DETAIL_HEAD_1, {
    border: true,
    font: { bold: true, size: 9 },
    align: { horizontal: 'center', vertical: 'middle', wrapText: true },
    height: 28,
  });
  r += 1;
  writeDocumentRow(ws, r, DETAIL_HEAD_2, {
    border: true,
    font: { bold: true, size: 9 },
    align: { horizontal: 'center', vertical: 'middle', wrapText: true },
  });
  ws.mergeCells(headR1, 5, headR1, 7);
  [1, 2, 3, 7, 8, 9, 10, 11, 12, 13].forEach((c) => {
    ws.mergeCells(headR1, c + DOCUMENT_COLUMN_OFFSET, headR1 + 1, c + DOCUMENT_COLUMN_OFFSET);
  });
  styleRange(ws, headR1, 2, headR1 + 1, 14, {
    border: BORDER,
    font: { bold: true, size: 9 },
    alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
  });
  r += 1;

  const detailBodyStartRow = r;
  for (const row of detailRows) {
    writeDocumentRow(ws, r, row, {
      border: true,
      align: { horizontal: 'center', vertical: 'middle', wrapText: true },
      font: { size: 9 },
    });
    styleRange(ws, r, 2, r, 14, { border: BORDER });
    r += 1;
  }
  // Gross / Net / Container: celdas fusionadas verticalmente (como rowSpan del PDF).
  const weightContainerCols = [12, 13, 14];
  for (const { startOutIndex, endOutIndex } of mergeSpans || []) {
    if (startOutIndex >= endOutIndex) continue;
    const rowFrom = detailBodyStartRow + startOutIndex;
    const rowTo = detailBodyStartRow + endOutIndex;
    for (const col of weightContainerCols) {
      ws.mergeCells(rowFrom, col, rowTo, col);
      ws.getCell(rowFrom, col).alignment = {
        horizontal: 'center',
        vertical: 'middle',
        wrapText: true,
      };
    }
  }

  const foot = [
    '',
    '',
    totalPaquetes != null && totalPaquetes !== '' ? String(totalPaquetes) : '',
    totalPaquetes != null && totalPaquetes !== '' ? `${totalPaquetes} BUNDLES` : '',
    '',
    '',
    '',
    '',
    '',
    sumM3 != null ? fmtNumCL(sumM3, 3) : '',
    sumPesoGross != null ? fmtNumCL(sumPesoGross, 3) : '',
    sumPesoNet != null ? fmtNumCL(sumPesoNet, 3) : '',
    '',
  ];
  writeDocumentRow(ws, r, foot, { border: true, font: { bold: true, size: 9 } });
  ws.mergeCells(r, 5, r, 7);
  styleRange(ws, r, 2, r, 14, { border: BORDER, font: { bold: true } });
  r += 2;

  writeDocumentRow(ws, r, ['Summary'], { font: { bold: true, size: 12 }, height: 20 });
  r += 1;
  const summaryStartRow = r;
  const summaryLabels = ['Total Bundles:', 'Total cbm or m³:', 'Total Gross Weight (Kg):'];
  const summaryValues = [
    totalPaquetes != null ? fmtIntegerCL(totalPaquetes) : '',
    sumM3 != null ? fmtNumCL(sumM3, 3) : '',
    sumPesoGross != null ? fmtNumCL(sumPesoGross, 3) : '',
  ];
  summaryLabels.forEach((label, index) => {
    const row = summaryStartRow + index;
    ws.getRow(row).height = 14;
    setPlainMergedCell(ws, row, 1, 2, label);
    setPlainMergedCell(ws, row, 3, 4, summaryValues[index], {
      alignment: { horizontal: 'right', vertical: 'top' },
    });
  });

  const summaryMiddleLines = resolvePackingListSummaryText({
    summaryAdditionalText,
    productDescription,
    paisOrigen,
    hsCode,
    lugarEntrega,
    nombrePuertoDestino,
    paisDestino,
    detalleExcel,
  })
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  summaryMiddleLines.forEach((line, index) => {
    const row = summaryStartRow + index;
    ws.getRow(row).height = Math.max(ws.getRow(row).height || 0, 14);
    setPlainMergedCell(ws, row, 6, 10, line);
  });

  const stampId = wb.addImage({
    base64: await imageUrlToDataUrl(summaryStampImage, 'image/png'),
    extension: 'png',
  });
  ws.addImage(stampId, {
    tl: { col: 11.25, row: summaryStartRow - 0.9 },
    ext: { width: 170, height: 84 },
    editAs: 'oneCell',
  });
  r = summaryStartRow + 4;

  styleRange(ws, 1, 1, r, 14, {
    fill: WHITE_FILL,
    font: { name: 'Arial', color: { argb: 'FF000000' } },
  });
  ws.pageSetup.printArea = `A1:N${r}`;

  return wb;
}

export async function generatePackingListExcel(params) {
  const wb = await buildPackingListExcelWorkbook(params);
  await workbookToDownload(wb, packingListExcelFileName(params));
}

export { packingListExcelFileName };
